// config
import { easings } from "@react-spring/web";
import { GridApi } from "ag-grid-community";

import Config from "../../Config";

export const anonymousAvatarUrl = "https://dev-media.performyard.com/static/img/anonymous.png";

export const checkColumnExists = (columns, value) => {
	for (let i = 0; i < columns[value.sectionID].length; i++) {
		if (columns[value.sectionID][i].field === value.id) {
			return true;
		}
	}

	return false;
};

// keep track of unique questions from selected reviews filter results
export const getUniqueQuestions = (dataSets) => {
	const uniqueQuestions = dataSets.find((dataSet) => dataSet.id === "uniqueQuestions")?.values ?? [];

	return uniqueQuestions;
};

export const getGroupedQuestions = (dataSets) => {
	let groupedQuestions = [];
	dataSets.forEach((dataSet) => {
		if (dataSet.id === "groupedQuestions") {
			groupedQuestions = dataSet.values.map((question) => ({
				...question,
				action: null,
			}));
		}
	});

	return groupedQuestions;
};

type CellRenderer =
	| "dateCellRenderer"
	| "peopleCellRenderer"
	| "personCellRenderer"
	| "reviewCycleCellRenderer"
	| "reviewTemplateCellRenderer"
	| "groupCellRenderer"
	| "booleanCellRenderer"
	| "reviewReportStatusCellRenderer"
	| "reviewCycleStatusCellRenderer"
	| "percentageCellRenderer"
	| "countCellRenderer"
	| "numericCellRenderer"
	| "superRowCellRenderer"
	| "goalCategoriesCellRenderer"
	| "goalOutlookCellRenderer"
	| "currentGoalProgressCellRenderer"
	| "goalTargetCellRenderer"
	| "goalStatusCellRenderer"
	| "goalTransparencyCellRenderer"
	| "goalCellRenderer"
	| "goalNameCellRenderer"
	| "reviewFormNameCellRenderer"
	| "surveyNameCellRenderer";

export const selectCellRendererType = ({
	type,
	isSummarizeByField,
	isSummaryReport,
	isGroupedQuestion,
}: {
	type: string;
	isSummarizeByField: boolean;
	isSummaryReport: boolean;
	isGroupedQuestion: boolean;
}): CellRenderer | "" => {
	// numeric summarize by fields hae binning logic already applied
	// and should be displayed as is
	if (Config.numericTypes.includes(type) && !isSummarizeByField) {
		return "numericCellRenderer";
	}

	switch (type) {
		case "date":
			return "dateCellRenderer";

		case "people":
			return "peopleCellRenderer";

		case "person":
			return "personCellRenderer";

		case "reviewCycle":
			return "reviewCycleCellRenderer";

		case "reviewTemplate":
			return "reviewTemplateCellRenderer";

		case "department":
			return "groupCellRenderer";

		case "boolean":
			return "booleanCellRenderer";

		case "reviewFormStatus":
			return "reviewReportStatusCellRenderer";

		case "reviewCycleStatus":
			return "reviewCycleStatusCellRenderer";

		case "percentage":
			return "percentageCellRenderer";

		case "multiple-select":
			return "superRowCellRenderer";

		case "multiple-choice":
			if (!isSummaryReport) {
				if (isGroupedQuestion) {
					return "superRowCellRenderer";
				}
				return "";
			}
			return "superRowCellRenderer";

		case "goalCategories":
			return "goalCategoriesCellRenderer";

		case "goalOutlook":
			return "goalOutlookCellRenderer";

		case "goalProgress":
			return "currentGoalProgressCellRenderer";

		case "goalTarget":
			return "goalTargetCellRenderer";

		case "goalStatus":
			return "goalStatusCellRenderer";

		case "goalTransparency":
			return "goalTransparencyCellRenderer";

		case "goalAlignment":
			return "goalCellRenderer";

		case "goalName":
			return "goalNameCellRenderer";

		case "reviewFormName":
			return "reviewFormNameCellRenderer";

		case "surveyName":
			return "surveyNameCellRenderer";

		default:
			return "";
	}
};

/**
 * A helper function to set the visibility of an array of columns
 */
export const setColumnVisibility = (gridApi: GridApi, columns: string[], visible: boolean) => {
	gridApi.setColumnsVisible(columns, visible);
};

/**
 * This function is responsible for getting the most recent column data from
 * the store and setting visibility of columns in the grid.
 */
export const recalculateColumnVisibility = (gridApi: GridApi, getSummaryColumnMetadata) => {
	const summaryColumnData = getSummaryColumnMetadata();
	const visibleColumns = Object.keys(summaryColumnData).filter((key) => summaryColumnData[key].isColumnVisible);
	const hiddenColumns = Object.keys(summaryColumnData).filter((key) => !summaryColumnData[key].isColumnVisible);

	setColumnVisibility(gridApi, visibleColumns, true);
	setColumnVisibility(gridApi, hiddenColumns, false);
};

/**
 * super row utils and configurations
 */
export const superRowConfig = {
	getWidths: (bodyWidth) => ({
		superRowWidth: bodyWidth - 33,
		questionWidth: bodyWidth - 40,
		answerWidth: bodyWidth - 70,
	}),
	getRowHeight: (params) => {
		const numRows = !params.data.isSummary ? params.data.answers?.length : new Set(params.data.answers || []).size;
		if (params.data.superRow) {
			const staticHeight = 66; // 66px: padding + borders plus question height
			const answerRowsHeight = 24 * numRows; // 24px: each answer row height,
			const marginHeight = 8 * (numRows - 1); // 8px: margin between answer rows
			return Math.min(staticHeight + answerRowsHeight + marginHeight, 316);
		} else {
			return undefined;
		}
	},
	getWidestStringWidth: (strings: string[]) => {
		const canvas = document.createElement("canvas");
		const context = canvas.getContext("2d");
		if (context) {
			// 14px Source Sans Pro, weight 600, line height 16px
			context.font = "600 14px Source Sans Pro";
			const widths = strings.map((string) => context.measureText(string).width);
			return Math.max(...widths);
		}
		return 0;
	},
	getRowAnimationConfig: (numRows) => ({
		from: { opacity: 0, marginTop: -Math.min(numRows * 32, 244) },
		to: { opacity: 1, marginTop: 0 },
		config: { duration: 450, easing: easings.easeOutCubic },
	}),
	getAnswerBarAnimationConfig: (width: number, fullWidth: number) => ({
		from: { width: fullWidth },
		to: { width },
		delay: 200,
		config: { duration: 300, easing: easings.easeOutQuart },
	}),
	numberToWidthInPixels: (num: number, total: number, width: number) => {
		const percentage = num / total;
		return percentage * width;
	},
};
